import React, { useState } from 'react';
import { content } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, Plane, Package, Globe2, CheckCircle, Calculator } from 'lucide-react';
import PricingCalculator from './PricingCalculator';

const Hero = () => {
  const { language } = useLanguage();
  const t = content[language]?.hero || content.en.hero;
  const [showCalculator, setShowCalculator] = useState(false);

  const scrollToSection = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        {/* Animated elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-500/5 rounded-full blur-3xl" />
        
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {/* Floating icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Plane className="absolute w-12 h-12 text-blue-400/10 animate-float" style={{ top: '15%', left: '10%' }} />
          <Package className="absolute w-10 h-10 text-cyan-400/10 animate-float" style={{ top: '70%', left: '15%', animationDelay: '1s' }} />
          <Globe2 className="absolute w-14 h-14 text-orange-400/10 animate-float" style={{ top: '25%', right: '15%', animationDelay: '2s' }} />
          <Package className="absolute w-8 h-8 text-blue-400/10 animate-float" style={{ top: '60%', right: '10%', animationDelay: '0.5s' }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Text */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-6">
              <Plane className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300">{t.subtitle}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 font-heading tracking-wide uppercase">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                {t.title}
              </span>
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-xl font-sans">
              {t.description}
            </p>

            {/* Features list */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                language === 'fr' ? 'Livraison 3-7 jours' : 'Delivery 3-7 days',
                language === 'fr' ? 'Suivi en temps réel' : 'Real-time tracking',
                language === 'fr' ? 'Assurance incluse' : 'Insurance included',
                language === 'fr' ? 'Support 24/7' : '24/7 Support'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => setShowCalculator(true)}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 h-14 text-lg group"
              >
                <Calculator className="w-5 h-5 mr-2" />
                {t.cta1}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('#services')}
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 h-14 text-lg"
              >
                {t.cta2}
              </Button>
            </div>
          </div>

          {/* Right side - Stats/Visual */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Main visual card */}
              <div className="relative bg-white/5 backdrop-blur-lg rounded-3xl border border-white/10 p-8">
                {/* World map placeholder */}
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-blue-900/50 to-slate-900/50 flex items-center justify-center relative overflow-hidden">
                  {/* Connection lines visualization */}
                  <div className="absolute inset-0">
                    <svg className="w-full h-full" viewBox="0 0 400 400">
                      {/* Canada point */}
                      <circle cx="100" cy="120" r="8" fill="#38bdf8" className="animate-pulse" />
                      <text x="100" y="105" fill="#94a3b8" fontSize="10" textAnchor="middle">Canada</text>
                      
                      {/* Africa points */}
                      <circle cx="280" cy="180" r="6" fill="#f97316" className="animate-pulse" style={{ animationDelay: '0.3s' }} />
                      <text x="280" y="165" fill="#94a3b8" fontSize="9" textAnchor="middle">Sénégal</text>
                      
                      <circle cx="300" cy="220" r="6" fill="#f97316" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
                      <text x="320" y="220" fill="#94a3b8" fontSize="9" textAnchor="start">Cameroun</text>
                      
                      <circle cx="290" cy="270" r="6" fill="#f97316" className="animate-pulse" style={{ animationDelay: '0.7s' }} />
                      <text x="290" y="290" fill="#94a3b8" fontSize="9" textAnchor="middle">Congo</text>
                      
                      {/* Connection lines */}
                      <path d="M108 120 Q200 100 272 180" stroke="url(#gradient1)" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-dash" />
                      <path d="M108 120 Q200 150 292 220" stroke="url(#gradient1)" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '0.5s' }} />
                      <path d="M108 120 Q200 200 282 270" stroke="url(#gradient1)" strokeWidth="2" fill="none" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '1s' }} />
                      
                      {/* Gradient definition */}
                      <defs>
                        <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#38bdf8" />
                          <stop offset="100%" stopColor="#f97316" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  
                  {/* Center logo/icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl">
                      <Plane className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">5+</div>
                    <div className="text-xs text-slate-400">{language === 'fr' ? 'Pays' : 'Countries'}</div>
                  </div>
                  <div className="text-center border-x border-white/10">
                    <div className="text-2xl font-bold text-white">3-7</div>
                    <div className="text-xs text-slate-400">{language === 'fr' ? 'Jours' : 'Days'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">24/7</div>
                    <div className="text-xs text-slate-400">Support</div>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                {language === 'fr' ? 'Standards DHL' : 'DHL Standards'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 50L48 45C96 40 192 30 288 35C384 40 480 60 576 65C672 70 768 60 864 50C960 40 1056 30 1152 35C1248 40 1344 60 1392 70L1440 80V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z" fill="white"/>
        </svg>
      </div>

      {/* Pricing Calculator Modal */}
      <PricingCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />
    </section>
  );
};

export default Hero;
